import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { IconMapPin, IconHome, IconDollarSign } from '../components/Icons';

const PropertyPromotionView = () => {
  const { t } = useLanguage();
  const [properties, setProperties] = useState([]);
  const [formData, setFormData] = useState({
    propertyName: '',
    monthlyRent: '',
    keyFeatures: '',
    virtualTourUrl: '',
    specialRepairProcedures: '',
  });
  const [aiDescription, setAiDescription] = useState('');
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapError, setMapError] = useState(false);

  // Leaflet dynamic loading
  useEffect(() => {
    if (!window.L) {
      // Leaflet CSS
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);

      // Leaflet JS
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => setMapLoaded(true);
      script.onerror = () => setMapError(true);
      document.body.appendChild(script);
    } else {
      setMapLoaded(true);
    }
  }, []);

  // Map initialization
  useEffect(() => {
    if (mapLoaded && window.L) {
      const mapContainer = document.getElementById('property-map');
      if (mapContainer && !mapContainer._leaflet_id) {
        try {
          const map = window.L.map('property-map').setView([14.5995, 120.9842], 11); // Manila coordinates
          
          window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '© OpenStreetMap contributors',
            maxZoom: 19
          }).addTo(map);

          // Add sample markers
          if (properties.length > 0) {
            properties.forEach((property) => {
              if (property.lat && property.lng) {
                window.L.marker([property.lat, property.lng])
                  .addTo(map)
                  .bindPopup(`<b>${property.propertyName}</b><br/>${t('monthlyRent')}: ${property.monthlyRent}`);
              }
            });
          } else {
            // Default marker when no properties
            window.L.marker([14.5995, 120.9842])
              .addTo(map)
              .bindPopup('<b>Manila, Philippines</b><br/>Register properties to show them here.');
          }

          // Refresh map on size change
          setTimeout(() => {
            map.invalidateSize();
          }, 100);
        } catch (error) {
          console.error('Map initialization error:', error);
          setMapError(true);
        }
      }
    }
  }, [mapLoaded, properties, t]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const generateAIDescription = async () => {
    if (!formData.keyFeatures) return;

    setIsGeneratingDescription(true);
    const apiKey = "AIzaSyDQbK0Pz7vqbFv9QrmZFZLLs-QuOxcLpeE"; // API key required for actual use

    try {
      const response = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${apiKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contents: [{
              parts: [{
                text: `다음 특징을 가진 부동산 매물에 대한 매력적인 설명을 작성해주세요: ${formData.keyFeatures}`
              }]
            }]
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAiDescription(data.candidates[0].content.parts[0].text);
      } else {
        setAiDescription(t('error'));
      }
    } catch (error) {
      setAiDescription(t('error'));
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newProperty = {
      ...formData,
      id: Date.now(),
      lat: 14.5995 + (Math.random() - 0.5) * 0.2, // Manila sample location
      lng: 120.9842 + (Math.random() - 0.5) * 0.2,
      status: 'available',
    };
    setProperties([...properties, newProperty]);
    setFormData({
      propertyName: '',
      monthlyRent: '',
      keyFeatures: '',
      virtualTourUrl: '',
      specialRepairProcedures: '',
    });
    setAiDescription('');
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('propertyPromotion')}</h1>

      {/* New Property Registration */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">{t('newPropertyRegistration')}</h2>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('propertyName')}
              </label>
              <input
                type="text"
                name="propertyName"
                value={formData.propertyName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('monthlyRent')}
              </label>
              <input
                type="text"
                name="monthlyRent"
                value={formData.monthlyRent}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('keyFeatures')}
              </label>
              <textarea
                name="keyFeatures"
                value={formData.keyFeatures}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('virtualTourUrl')} ({t('optional')})
              </label>
              <input
                type="url"
                name="virtualTourUrl"
                value={formData.virtualTourUrl}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('specialRepairProcedures')} ({t('optional')})
              </label>
              <input
                type="text"
                name="specialRepairProcedures"
                value={formData.specialRepairProcedures}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* AI Property Description Generation */}
          <div className="mt-4">
            <button
              type="button"
              onClick={generateAIDescription}
              disabled={!formData.keyFeatures || isGeneratingDescription}
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGeneratingDescription ? t('generatingDescription') : t('generateAIDescription')}
            </button>
          </div>

          {aiDescription && (
              <div className="mt-4 p-4 bg-gray-50 rounded-md">
                <h3 className="font-semibold mb-2">{t('aiGeneratedDescription')}</h3>
                {/* 여기에 whitespace-pre-line 클래스를 추가합니다. */}
                <p className="text-gray-700 whitespace-pre-line">{aiDescription}</p>
              </div>
          )}

          <div className="mt-6">
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-md"
            >
              {t('finalizeRegistration')}
            </button>
          </div>
        </form>
      </div>

      {/* My Listings */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">{t('myListings')}</h2>
        {properties.length === 0 ? (
          <p className="text-gray-500">{t('noListings')}</p>
        ) : (
          <div className="space-y-4">
            {properties.map((property) => (
              <div key={property.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-md">
                <div className="flex items-center space-x-4">
                  <IconHome className="w-6 h-6 text-gray-600" />
                  <div>
                    <h3 className="font-semibold">{property.propertyName}</h3>
                    <p className="text-sm text-gray-600">{t('monthlyRent')}: {property.monthlyRent}</p>
                  </div>
                </div>
                <button className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-md">
                  {t('manage')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Property Map */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">{t('propertyMap')}</h2>
        {mapError ? (
          <p className="text-red-500">{t('mapLoadError')}</p>
        ) : !mapLoaded ? (
          <p className="text-gray-500">{t('loadingMap')}</p>
        ) : (
          <div id="property-map" className="w-full" style={{ height: '400px', minHeight: '400px' }}></div>
        )}
      </div>
    </div>
  );
};

export default PropertyPromotionView;
